import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { tenantScope } from "../../lib/tenantScope";

import { findAssistTarget, markAssistTarget, type AssistTarget } from "./assist-target";
import { guideContext, guideHref, resumeGuideHref, isStartRequestPath } from "./guide-context";
import { GuideSurface } from "./GuideSurface";

type Context = ReturnType<typeof guideContext>;
const button =
  "min-h-11 rounded border border-slate-300 px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-brand-500";
function read(key: string) {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}
function save(key: string, value: boolean) {
  try {
    sessionStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* Optional UI preference only. */
  }
}
export function useHelpVisibility(userId?: string, scope?: string | null) {
  const key = `kv-help-open-v1:${userId}:${scope}`;
  const [state, setState] = useState(() => ({ key, open: read(key) }));
  const open = state.key === key ? state.open : read(key);
  const setOpen = (next: boolean) => {
    save(key, next);
    setState({ key, open: next });
  };
  return { open, setOpen };
}
export function HelpSidebar({ onClose }: { onClose: () => void }) {
  const { user, membershipStatus } = useAuth();
  return (
    <ScopedHelpSidebar key={`${user?.id}:${tenantScope()}:${membershipStatus}`} onClose={onClose} />
  );
}
function ScopedHelpSidebar({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { user, membershipStatus } = useAuth();
  const scope = tenantScope();
  const location = useLocation();
  const navigate = useNavigate();
  const modeKey = `kv-help-assist-v1:${user?.id}:${scope}`;
  const [assisted, setAssisted] = useState(() => read(modeKey));
  const [collapsed, setCollapsed] = useState(false);
  const contextKey = `kv-help-context-v1:${user?.id}:${scope}`;
  const routeKey = location.pathname + location.search;
  const starting = isStartRequestPath(location.pathname);
  const [selection, setSelection] = useState(() => {
    let href = resumeGuideHref(user?.id, scope);
    try {
      href = sessionStorage.getItem(contextKey) || href;
    } catch {
      /* Optional context. */
    }
    let saved: URL;
    try {
      saved = new URL(href, "https://guide.invalid");
    } catch {
      saved = new URL("https://guide.invalid/help");
    }
    const route = guideContext(location.pathname, location.search);
    const context =
      starting || route.projectId || route.requestId || route.companyId
        ? route
        : guideContext(saved.pathname, saved.search);
    return { routeKey, context };
  });
  const route = guideContext(location.pathname, location.search);
  const context = starting
    ? route
    : selection.routeKey !== routeKey &&
        (route.projectId ||
          route.requestId ||
          (route.companyId && route.companyId !== selection.context.companyId))
      ? route
      : selection.context;
  const [pending, setPending] = useState<{ to: string; target: AssistTarget } | null>(null);
  const [notice, setNotice] = useState<"found" | "missing" | "waiting" | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const previousRoute = useRef(routeKey);
  useEffect(() => {
    if (previousRoute.current === routeKey) return;
    previousRoute.current = routeKey;
    const expected = pending ? new URL(pending.to, "https://guide.invalid") : null;
    const params = new URLSearchParams(location.search);
    const arrivingAtTarget =
      expected &&
      location.pathname === expected.pathname &&
      [...expected.searchParams].every(([key, value]) => params.get(key) === value);
    if (!arrivingAtTarget) {
      setPending(null);
      setNotice(null);
    }
  }, [routeKey, pending, location.pathname, location.search]);
  const active = !!scope && membershipStatus === "active";
  useEffect(() => {
    if (!active) {
      setPending(null);
      setNotice(null);
    }
  }, [active]);
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => {
    if (selection.routeKey !== routeKey) setSelection({ routeKey, context });
  }, [routeKey, context, selection.routeKey]);
  const contextHref = guideHref(context.projectId, context.requestId, context.companyId);
  const [validatedHref, setValidatedHref] = useState<string | null>(null);
  const validateContext = useCallback(
    (valid: boolean) => {
      setValidatedHref(valid ? contextHref : null);
      try {
        if (valid) sessionStorage.setItem(contextKey, contextHref);
        else {
          sessionStorage.removeItem(contextKey);
          // A rejected resume must not reintroduce the same stale IDs on remount.
          if (resumeGuideHref(user?.id, scope) === guideHref(context.projectId, context.requestId))
            sessionStorage.removeItem(`kv-guide-context-v1:${user?.id}:${scope}`);
        }
      } catch {
        /* Optional reading context only. */
      }
    },
    [contextHref, contextKey, context.projectId, context.requestId, user?.id, scope],
  );
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        !event.defaultPrevented &&
        !document.querySelector('[role="dialog"][aria-modal="true"]')
      ) {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [onClose]);
  useEffect(() => {
    if (!pending || !active || !assisted) return;
    const expected = new URL(pending.to, "https://guide.invalid");
    const params = new URLSearchParams(location.search);
    const matches =
      location.pathname === expected.pathname &&
      [...expected.searchParams].every(([k, v]) => params.get(k) === v);
    let clearMark: (() => void) | undefined;
    let marked: HTMLElement | null = null;
    let disposed = false;
    const main = document.getElementById("main");
    const locate = () => {
      if (disposed || !matches || !main) return;
      if (clearMark) {
        if (
          !marked?.isConnected ||
          !marked.getClientRects().length ||
          marked.matches(':disabled,[aria-disabled="true"]') ||
          marked.closest('[hidden],[inert],[aria-hidden="true"],[aria-disabled="true"]')
        ) {
          clearMark();
          clearMark = undefined;
          disposed = true;
          observer.disconnect();
          setNotice("missing");
        }
        return;
      }
      const target = findAssistTarget(main, pending.target);
      if (target) {
        marked = target;
        clearMark = markAssistTarget(target);
        setNotice("found");
      }
    };
    const observer = new MutationObserver(locate);
    if (main)
      observer.observe(main, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["hidden", "inert", "aria-hidden", "disabled", "aria-disabled"],
      });
    const frame = requestAnimationFrame(locate);
    const timeout = window.setTimeout(() => {
      if (!clearMark) {
        setNotice("missing");
        observer.disconnect();
      }
    }, 5000);
    return () => {
      disposed = true;
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      clearMark?.();
    };
  }, [pending, location.pathname, location.search, active, assisted]);
  const choose = (value: Context) => {
    setSelection({ routeKey, context: value });
    setPending(null);
    setNotice(null);
  };
  const show = (to: string, target: AssistTarget, value: Context) => {
    if (!active || !assisted) return;
    choose(value);
    setNotice("waiting");
    setPending({ to, target });
    if (window.matchMedia("(max-width: 1023px)").matches) setCollapsed(true);
    if (to !== location.pathname + location.search) navigate(to);
  };
  return (
    <aside
      id="contextual-help"
      aria-label={t("assist.title")}
      className="border-brand-200 min-w-0 shrink-0 border-b bg-white lg:w-[360px] lg:overflow-y-auto lg:border-b-0 lg:border-l print:hidden"
    >
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-white p-3">
        <h2
          ref={heading}
          tabIndex={-1}
          className="min-w-0 text-base font-semibold focus-visible:ring-2"
        >
          {t("assist.title")}
        </h2>
        <div className="flex gap-1">
          <button
            type="button"
            className={`${button} lg:hidden`}
            aria-expanded={!collapsed}
            aria-controls="help-sidebar-body"
            onClick={() => setCollapsed(!collapsed)}
          >
            {t(collapsed ? "assist.expand" : "assist.collapse")}
          </button>
          <button type="button" className={button} onClick={onClose}>
            {t("guide.close")}
          </button>
        </div>
      </div>
      {notice && (
        <p role="status" className="bg-brand-50 border-b p-3 text-sm">
          {t(`assist.${notice}`)}
        </p>
      )}
      <div id="help-sidebar-body" className={`${collapsed ? "hidden lg:block" : "block"} p-3`}>
        {!active ? (
          <p role="status">{t("guide.unavailable")}</p>
        ) : (
          <>
            <label className="mb-3 flex min-h-11 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={assisted}
                onChange={(e) => {
                  setAssisted(e.target.checked);
                  save(modeKey, e.target.checked);
                  setPending(null);
                  setNotice(null);
                }}
              />
              {t("assist.enable")}
            </label>
            {location.pathname === "/settings/team" && (
              <p className="mb-3 text-sm">{t("guide.team_context")}</p>
            )}
            <GuideSurface
              context={context}
              onContextValidation={validateContext}
              presentation="sidebar"
              onContextChange={choose}
              onShowStep={assisted ? show : undefined}
              onReadingChange={() => {
                setPending(null);
                setNotice(null);
              }}
            />
            <Link
              className={`${button} mt-3 inline-flex`}
              state={{
                returnTo: location.pathname + location.search + location.hash,
                userId: user?.id,
                tenantId: scope,
              }}
              to={validatedHref === contextHref ? contextHref : "/help"}
            >
              {t("assist.full_guide")}
            </Link>
          </>
        )}
      </div>
    </aside>
  );
}
